import React from "react";
import { v4 as uuidv4 } from "uuid";
import { observer } from "mobx-react";
import {
    ConditionRuleType,
    FieldOptionsType,
    StatementType,
} from "../../types";
import StatementHandler from "./StatementHandler";
import { emptyConditionIndicator } from "../../helpers/emptyStates";

type StatementContainerProps = {
    statement: StatementType;
    ruleId: string;
    conditionRules: ConditionRuleType[];
    setConditionRules: (conditionRules: ConditionRuleType[]) => void;
    fieldOptions: FieldOptionsType[];
    isElseTree?: boolean;
};

const StatementContainer: React.FC<StatementContainerProps> = observer(
    ({
        statement,
        ruleId,
        conditionRules,
        setConditionRules,
        fieldOptions,
        isElseTree,
    }) => {
        // returns the entire tree from the current statement with the target statement updated
        // Defines the level property for indentation / max depth of the tree
        const updateStatement = (
            runUpdate: Function,
            currentStatement?: StatementType,
            targetStatement?: StatementType,
            shouldDelete?: boolean,
            level = 1,
        ) => {
            if (!targetStatement || !currentStatement) {
                return undefined;
            }
            // statement found
            if (targetStatement.id === currentStatement.id) {
                if (shouldDelete) {
                    return undefined;
                }

                return {
                    ...targetStatement,
                    level,
                    ...runUpdate(targetStatement),
                };
            }

            return {
                ...currentStatement,
                then: updateStatement(
                    runUpdate,
                    currentStatement.then,
                    targetStatement,
                    shouldDelete,
                    level + 1,
                ),
                else: updateStatement(
                    runUpdate,
                    currentStatement.else,
                    targetStatement,
                    shouldDelete,
                    level + 1,
                ),
            };
        };

        const updateRule = (
            runUpdate: Function,
            targetStatement?: StatementType,
            shouldDelete?: boolean,
        ) => {
            setConditionRules(
                conditionRules.map((rule) => {
                    if (rule.id === ruleId) {
                        return {
                            ...rule,
                            ...(isElseTree
                                ? {
                                      else: updateStatement(
                                          runUpdate,
                                          rule.else,
                                          targetStatement,
                                          shouldDelete,
                                      ),
                                  }
                                : {
                                      then: updateStatement(
                                          runUpdate,
                                          rule.then,
                                          targetStatement,
                                          shouldDelete,
                                      ),
                                  }),
                        };
                    }
                    return rule;
                }),
            );
        };

        const addConditionIndicator = (targetStatement?: StatementType) => {
            const updater = (statement: StatementType) => {
                return {
                    conditionIndicators: [
                        ...(statement.conditionIndicators || []),
                        emptyConditionIndicator(),
                    ],
                };
            };

            updateRule(updater, targetStatement);
        };

        const removeConditionIndicator = (
            id: string,
            targetStatement?: StatementType,
        ) => {
            const updater = (statement: StatementType) => {
                return {
                    conditionIndicators: statement.conditionIndicators?.filter(
                        (indicator) => indicator.id !== id,
                    ),
                };
            };

            updateRule(updater, targetStatement);
        };

        const updateConditionIndicator = (
            indicatorId: string,
            key: string,
            value: string | number,
            targetStatement?: StatementType,
        ) => {
            const updater = (statement: StatementType) => {
                return {
                    conditionIndicators: statement.conditionIndicators?.map(
                        (indicator) => {
                            if (indicator.id === indicatorId) {
                                return {
                                    ...indicator,
                                    [key]: value,
                                };
                            }
                            return indicator;
                        },
                    ),
                };
            };

            updateRule(updater, targetStatement);
        };

        const updateResult = (
            value?: {
                result?: string;
                isLiteral: boolean;
                sourceName?: string;
            },
            targetStatement?: StatementType,
        ) => {
            const updater = () => {
                return {
                    ...value,
                };
            };
            updateRule(updater, targetStatement);
        };

        const removeResult = (targetStatement?: StatementType) => {
            updateResult(undefined, targetStatement);
        };

        const addResult = (targetStatement?: StatementType) => {
            const updater = (statement: StatementType) => {
                return {
                    ...statement,
                    ...(!statement.conditionIndicators?.length
                        ? { result: "", isLiteral: true }
                        : {
                              then: {
                                  id: uuidv4(),
                                  result: "",
                                  isLiteral: true,
                              },
                          }),
                };
            };
            updateRule(updater, targetStatement);
        };

        const addElseResult = (targetStatement?: StatementType) => {
            const updater = (statement: StatementType) => {
                return {
                    ...statement,
                    else: {
                        id: uuidv4(),
                        result: "",
                        isLiteral: true,
                    },
                };
            };

            updateRule(updater, targetStatement);
        };

        const addChildStatement = (
            targetStatement?: StatementType,
            isElse = false,
        ) => {
            const newThenStatement: StatementType = {
                id: uuidv4(),
                conditionIndicators: [emptyConditionIndicator()],
                hasError: false,
            };

            const updater = (statement: StatementType) => {
                return {
                    ...statement,
                    ...(!isElse
                        ? { then: newThenStatement }
                        : { then: statement.then }),
                    ...(isElse
                        ? { else: newThenStatement }
                        : { else: statement.else }),
                };
            };

            updateRule(updater, targetStatement);
        };

        const removeSelf = (
            targetStatement?: StatementType,
            isTopLevelThenStatement = false,
        ) => {
            // This will only run if the statement is the top level then statement
            // The API requires that the top level then statement is defined so we don't remove it.
            const updater = (statement: StatementType) => {
                return {
                    ...statement,
                    conditionIndicators: [],
                    result: undefined,
                    sourceName: undefined,
                    then: undefined,
                    else: undefined,
                };
            };
            updateRule(updater, targetStatement, !isTopLevelThenStatement);
        };

        return (
            <StatementHandler
                statement={statement}
                fieldOptions={fieldOptions}
                addConditionIndicator={addConditionIndicator}
                removeConditionIndicator={removeConditionIndicator}
                updateConditionIndicator={updateConditionIndicator}
                addResult={addResult}
                removeResult={removeResult}
                updateResult={updateResult}
                addChildStatement={addChildStatement}
                addElseResult={addElseResult}
                removeSelf={removeSelf}
                isTopLevelThenStatement={!isElseTree}
                isTopLevelElseStatement={isElseTree}
                isElseStatement={isElseTree}
                isElseTree={isElseTree}
            />
        );
    },
);

export default StatementContainer;
